/**
 * TransferDialog — opens when softphone.ts dispatches `apex:show-transfer-dialog`.
 * Lists available agents (via `cmd.transfer_targets` on the voice WS) and
 * invokes warm/cold transfer through window.ApexSoftphone.
 */

import { useCallback, useEffect, useState } from 'react';
import { request as voiceRequest } from '../../../lib/voice-ws';
import { toE164 } from '../../../lib/phone-format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';

interface TransferTarget {
  user_id: string;
  name: string;
  status: string;
  tenant_id: string;
  updated_at: number;
}

interface SoftphoneTransferApi {
  warmTransfer: (target: string) => Promise<void>;
  coldTransfer: (target: string) => Promise<void>;
}

function getSoftphone(): SoftphoneTransferApi | null {
  return (window as unknown as Record<string, unknown>)[
    'ApexSoftphone'
  ] as SoftphoneTransferApi | null;
}

function sortTargets(list: TransferTarget[]): TransferTarget[] {
  // Available first, then by name. Busy/dnd/offline drop below available
  // so the rep's eye lands on the people they can actually transfer to.
  const rank = (s: string) => (s === 'available' ? 0 : s === 'busy' ? 1 : s === 'dnd' ? 2 : 3);
  return [...list].sort((a, b) => {
    const r = rank(a.status) - rank(b.status);
    if (r !== 0) return r;
    return a.name.localeCompare(b.name);
  });
}

function statusDotClass(status: string): string {
  if (status === 'available') return 'bg-brand-green';
  if (status === 'busy') return 'bg-amber-500';
  if (status === 'dnd') return 'bg-destructive/60';
  return 'bg-muted-foreground/40';
}

/**
 * "5m" / "2h" / "3d" — terse time-since string for the time-in-status
 * column. `updatedAt` is unix milliseconds (apex-voice presence.go uses
 * `time.Now().UnixMilli()`).
 */
function formatTimeSince(updatedAt: number): string {
  if (!Number.isFinite(updatedAt)) return '';
  const diffMs = Date.now() - updatedAt;
  if (diffMs < 0) return 'now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    const remMin = min % 60;
    return remMin > 0 ? `${hr}h ${remMin}m` : `${hr}h`;
  }
  const days = Math.floor(hr / 24);
  return `${days}d`;
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function TransferDialog() {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<TransferTarget[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [external, setExternal] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Default the picker to ONLY available reps (signed in + clocked in +
  // status='available'). Busy/dnd/offline reps are hidden behind the
  // "Show all" toggle so they're not in the rep's way during a normal
  // transfer but still reachable when needed.
  const [availableOnly, setAvailableOnly] = useState(true);
  // Tick every 30s so the time-in-status column doesn't go stale while
  // the dialog stays open.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('apex:show-transfer-dialog', handler);
    return () => window.removeEventListener('apex:show-transfer-dialog', handler);
  }, []);

  // Reset transient state on close; fetch targets on open
  useEffect(() => {
    if (!open) {
      setTargets(null);
      setLoadError(false);
      setSelectedAgent(null);
      setExternal('');
      setError(null);
      setSubmitting(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await voiceRequest<{ targets?: TransferTarget[] }>('cmd.transfer_targets', {});
        if (cancelled) return;
        setTargets(sortTargets(resp.targets ?? []));
      } catch {
        if (cancelled) return;
        setLoadError(true);
        setTargets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const doTransfer = useCallback(async (target: string, type: 'cold' | 'warm') => {
    if (!target) return;
    const sp = getSoftphone();
    if (!sp) {
      setError('Softphone unavailable');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const fn = type === 'cold' ? sp.coldTransfer : sp.warmTransfer;
      await fn(target);
      setOpen(false);
    } catch (err) {
      setError(errorMessage(err, 'Transfer failed'));
    } finally {
      setSubmitting(false);
    }
  }, []);

  const doAgentTransfer = useCallback(
    (userId: string, type: 'cold' | 'warm') => {
      void doTransfer(userId, type);
    },
    [doTransfer],
  );

  const doExternalTransfer = useCallback(
    (type: 'cold' | 'warm') => {
      const normalized = toE164(external);
      if (!normalized) {
        setError('Enter a valid phone number');
        return;
      }
      void doTransfer(normalized, type);
    },
    [external, doTransfer],
  );

  const loading = open && targets === null;
  // visibleTargets respects the availableOnly toggle. When that toggle is
  // on, busy/dnd/offline reps are hidden — see hiddenCount below for the
  // count we expose in the toggle label.
  const visibleTargets =
    targets == null
      ? null
      : availableOnly
        ? targets.filter((t) => t.status === 'available')
        : targets;
  const hasAgents = (visibleTargets?.length ?? 0) > 0;
  const hiddenCount = (targets?.length ?? 0) - (visibleTargets?.length ?? 0);
  const externalDisabled = submitting || !external.trim();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer Call</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-3 py-6 text-muted-foreground">
            <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin" />
            <span className="text-sm">Loading agents...</span>
          </div>
        )}

        {!loading && targets && targets.length > 0 && (
          <div className="flex items-center justify-between px-1 -mb-1">
            <span className="text-[0.6875rem] uppercase tracking-wider text-muted-foreground">
              {availableOnly ? 'Available reps' : 'All reps'}
            </span>
            <button
              type="button"
              onClick={() => {
                setSelectedAgent(null);
                setAvailableOnly((v) => !v);
              }}
              className="text-[0.6875rem] text-primary hover:text-primary/80 cursor-pointer"
            >
              {availableOnly
                ? hiddenCount > 0
                  ? `Show all (+${hiddenCount})`
                  : 'Show all'
                : 'Available only'}
            </button>
          </div>
        )}

        {!loading && hasAgents && (
          <div className="flex flex-col gap-1 max-h-64 overflow-y-auto -mx-2 px-2">
            {visibleTargets?.map((t) => {
              const offline = t.status === 'offline';
              const isSelected = selectedAgent === t.user_id;
              const timeInStatus = formatTimeSince(t.updated_at);
              return (
                <div
                  key={t.user_id}
                  className={`rounded-md border transition-colors ${
                    isSelected
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-transparent hover:bg-accent'
                  } ${offline ? 'opacity-50' : ''}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setSelectedAgent(isSelected ? null : t.user_id);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer"
                    aria-expanded={isSelected}
                  >
                    <span className={`w-2 h-2 rounded-full ${statusDotClass(t.status)}`} />
                    <span className="flex-1 text-sm font-medium truncate">{t.name}</span>
                    <span className="text-[0.6875rem] text-muted-foreground flex items-center gap-1.5">
                      <span className="uppercase tracking-wider">{t.status}</span>
                      {timeInStatus && (
                        <>
                          <span>·</span>
                          <span className="tabular-nums">{timeInStatus}</span>
                        </>
                      )}
                    </span>
                  </button>
                  {isSelected && (
                    <div className="flex gap-2 px-3 pb-2">
                      <button
                        type="button"
                        onClick={() => doAgentTransfer(t.user_id, 'cold')}
                        disabled={submitting}
                        className="flex-1 h-8 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium cursor-pointer"
                      >
                        Cold Transfer
                      </button>
                      <button
                        type="button"
                        onClick={() => doAgentTransfer(t.user_id, 'warm')}
                        disabled={submitting}
                        className="flex-1 h-8 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium cursor-pointer"
                      >
                        Warm Transfer
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* No-agent empty states. Two distinct reasons we land here:
              1. The presence service returned ZERO targets — no other reps
                 signed in. Without this notice the dialog just shows the
                 external-number input with no context, which made it look
                 like the rep picker was missing.
              2. There ARE reps but the availableOnly filter hid them all.
                 Pointer at the "Show all" toggle. */}
        {!loading && !loadError && targets?.length === 0 && (
          <div className="text-[0.75rem] text-muted-foreground italic px-3 py-3 rounded-md bg-secondary/30 border border-border/40">
            No other reps are signed in right now — use an external number below.
          </div>
        )}
        {!loading && targets && targets.length > 0 && !hasAgents && availableOnly && (
          <div className="text-[0.75rem] text-muted-foreground italic px-3 py-3 rounded-md bg-secondary/30 border border-border/40">
            No reps are currently available for transfer. Click <em>Show all</em> above or use an
            external number below.
          </div>
        )}

        {!loading && (
          <div className="flex flex-col gap-2">
            {(hasAgents ||
              loadError ||
              (targets?.length ?? 0) === 0 ||
              (targets && targets.length > 0 && !hasAgents && availableOnly)) && (
              <div className="flex items-center gap-2 text-[0.6875rem] uppercase tracking-wider text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                <span>External Number</span>
                <span className="h-px flex-1 bg-border" />
              </div>
            )}
            <Input
              type="tel"
              placeholder="Enter phone number..."
              value={external}
              onChange={(e) => {
                setExternal(e.target.value);
                if (error) setError(null);
              }}
              autoComplete="off"
            />
            <p className="text-[0.6875rem] text-muted-foreground">
              US: 10 digits (e.g. 5551234567) or international with country code (+15551234567)
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => doExternalTransfer('cold')}
                disabled={externalDisabled}
                className="flex-1 h-9 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium cursor-pointer"
              >
                Cold Transfer
              </button>
              <button
                type="button"
                onClick={() => doExternalTransfer('warm')}
                disabled={externalDisabled}
                className="flex-1 h-9 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium cursor-pointer"
              >
                Warm Transfer
              </button>
            </div>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
